import { useState, useEffect } from "react";
import api, { API } from "@/lib/api";
import { PageHeader, Panel, Loading, Tag, StatusBadge } from "@/components/common";
import { DeviceMobile, Plus, Sparkle, Spinner, RocketLaunch, Storefront, CurrencyGbp,
  Image as ImageIcon, CheckCircle, WarningCircle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = ["Overview", "Store Listing", "Monetization", "Marketing", "Release Center"];
const MKT_KINDS = [
  { key: "icon", label: "App Icon" }, { key: "feature_graphic", label: "Feature Graphic" },
  { key: "screenshot", label: "Screenshot" }, { key: "ad_still", label: "Ad Still" },
];

export default function AppStudio() {
  const [apps, setApps] = useState(null);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = () => api.get("/apps").then(({ data }) => setApps(data));
  useEffect(() => { load(); }, []);

  const createApp = async () => {
    if (!newName.trim()) { toast.error("App name required"); return; }
    setCreating(true);
    try { await api.post("/apps", { data: { name: newName } }); toast.success("App created"); setNewName(""); load(); }
    catch { toast.error("Could not create app"); } finally { setCreating(false); }
  };

  if (sel) return <AppDetail app={sel} tab={tab} setTab={setTab} onBack={() => { setSel(null); load(); }} />;

  return (
    <div>
      <PageHeader icon={DeviceMobile} title="App Studio"
        subtitle="Manage real mobile app projects — build, store listing, monetization, marketing & Google Play publishing. Reuses your studio's AI, storage, secrets & billing." />
      <Panel className="mb-8">
        <div className="flex flex-wrap gap-3 items-center">
          <input data-testid="app-name" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="New app name (e.g. PinPilot)"
            className="flex-1 min-w-[220px] rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-white outline-none focus:border-[#276A6B]" />
          <button data-testid="app-create" onClick={createApp} disabled={creating}
            className="inline-flex items-center gap-2 rounded-full bg-[#276A6B] px-5 py-2.5 font-semibold text-white hover:bg-[#174A4B] transition-colors disabled:opacity-60">
            {creating ? <Spinner size={18} className="animate-spin" /> : <Plus size={18} weight="bold" />} Create App
          </button>
        </div>
      </Panel>
      {!apps ? <Loading /> : apps.length === 0 ? <p className="text-sm text-zinc-500">No apps yet — create your first.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {apps.map((a) => (
            <button key={a.id} data-testid={`app-card-${a.id}`} onClick={() => { setSel(a); setTab("Overview"); }}
              className="text-left rounded-2xl border border-white/8 p-5 panel-hover hover:border-[#6FA9A5]/50 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: "#276A6B" }}>
                  <DeviceMobile size={22} weight="fill" className="text-[#F5F1E8]" />
                </div>
                <div>
                  <div className="font-display text-lg font-bold text-white">{a.name}</div>
                  <div className="text-xs text-zinc-500">{a.category}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                <StatusBadge status={a.status} />
                <Tag>{a.platform}</Tag>
                <Tag>{a.framework}</Tag>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function AppDetail({ app, tab, setTab, onBack }) {
  const [a, setA] = useState(app);
  const [pre, setPre] = useState(null);
  const [genning, setGenning] = useState(null);
  const [mkt, setMkt] = useState([]);
  const brand = a.brand?.colors || {};

  const refresh = () => api.get(`/apps/${a.id}`).then(({ data }) => setA(data));
  useEffect(() => {
    api.get(`/apps/${a.id}/preflight`).then(({ data }) => setPre(data)).catch(() => {});
    api.get(`/assets?kind=marketing`).then(({ data }) => setMkt((data.assets || []).filter(x => x.app_id === a.id))).catch(() => {});
  }, [a.id]);

  const genMarketing = async (kind) => {
    setGenning(kind);
    try {
      const { data } = await api.post(`/apps/${a.id}/marketing`, { data: { kind, prompt: `PinPilot ${kind} showing the label scan and navigation for delivery drivers` } });
      toast.success(`${kind} generated & saved to Asset Library`);
      setMkt((m) => [data.asset, ...m]);
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); } finally { setGenning(null); }
  };

  return (
    <div>
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft size={16} /> App Studio</button>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: brand.Primary || "#276A6B" }}>
          <DeviceMobile size={26} weight="fill" style={{ color: brand.Cream || "#F5F1E8" }} />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-white">{a.name}</h2>
          <p className="text-sm text-zinc-500">{a.category} · {a.package_id || "package id not set"}</p>
        </div>
        <div className="ml-auto"><StatusBadge status={a.status} /></div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-white/8 pb-3">
        {TABS.map((t) => (
          <button key={t} data-testid={`app-tab-${t.replace(/ /g, '-').toLowerCase()}`} onClick={() => setTab(t)}
            className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${tab === t ? "border-[#6FA9A5]/60 bg-[#276A6B]/20 text-[#6FA9A5]" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid md:grid-cols-2 gap-5">
          <Panel>
            <h3 className="font-display font-bold text-white mb-3">Brand — {a.brand?.direction || "—"}</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(brand).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 rounded-lg border border-white/10 px-2 py-1">
                  <span className="h-5 w-5 rounded" style={{ background: v }} />
                  <span className="text-xs text-zinc-300">{k}</span><span className="text-[10px] text-zinc-500">{v}</span>
                </div>
              ))}
            </div>
          </Panel>
          <Panel>
            <h3 className="font-display font-bold text-white mb-3">Core workflow</h3>
            <p className="text-sm text-zinc-300">Scan/Search → Address → Map → Correct Pin → Navigate / Fly to Location (PRO) → Save Entrance</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              <Tag>Label Scan</Tag><Tag>Saved Entrances</Tag><Tag>Driver Mode</Tag><Tag>Fly to Location · PRO</Tag>
            </div>
            <p className="mt-4 text-xs text-[#FFB800]">Native Flutter app codebase + AAB build are Phase 2 (needs a cloud build host — can't compile here).</p>
          </Panel>
        </div>
      )}

      {tab === "Store Listing" && <ListingTab a={a} refresh={refresh} />}
      {tab === "Monetization" && <MonetizationTab a={a} />}

      {tab === "Marketing" && (
        <div>
          <Panel className="mb-5">
            <h3 className="font-display font-bold text-white mb-1">App Marketing Studio</h3>
            <p className="text-xs text-zinc-500 mb-4">Generate real, on-brand marketing assets via fal.ai (uses PinPilot brand memory). Saved to your Asset Library.</p>
            <div className="flex flex-wrap gap-2">
              {MKT_KINDS.map((k) => (
                <button key={k.key} data-testid={`mkt-gen-${k.key}`} onClick={() => genMarketing(k.key)} disabled={genning === k.key}
                  className="inline-flex items-center gap-2 rounded-full bg-[#276A6B]/20 border border-[#276A6B]/50 text-[#6FA9A5] px-4 py-2 text-sm font-semibold hover:bg-[#276A6B]/30 disabled:opacity-60">
                  {genning === k.key ? <Spinner size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} {k.label}
                </button>
              ))}
            </div>
          </Panel>
          {mkt.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {mkt.map((m) => (
                <div key={m.id} className="rounded-xl overflow-hidden border border-white/10">
                  <img src={`${API}${m.url}`} alt={m.marketing_kind} className="w-full aspect-square object-cover" />
                  <div className="px-2 py-1 text-[11px] text-zinc-400">{m.marketing_kind}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Release Center" && (
        <Panel>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-white">Google Play Pre-flight</h3>
            {pre && <Tag className={pre.store_connection === "CONNECTED" ? "text-[#00FF9D]" : "text-[#FF3366] border-[#FF3366]/40"}>Store: {pre.store_connection}</Tag>}
          </div>
          {!pre ? <Loading /> : (
            <>
              <div className="text-sm text-zinc-400 mb-3">{pre.passed}/{pre.total} checks pass · <span className="text-[#FFB800]">Submission stays disabled until Google Play is connected and a signed AAB exists — we never claim approval.</span></div>
              <ul className="space-y-1.5 mb-4">
                {pre.checks.map((c) => (
                  <li key={c.key} className="flex items-center gap-2 text-sm">
                    {c.pass ? <CheckCircle size={16} weight="fill" className="text-[#00FF9D]" /> : <WarningCircle size={16} weight="fill" className={c.manual ? "text-[#FFB800]" : "text-zinc-600"} />}
                    <span className={c.pass ? "text-zinc-300" : "text-zinc-400"}>{c.label}</span>
                    {c.manual && !c.pass && <span className="text-[10px] text-[#FFB800] ml-1">MANUAL ACTION REQUIRED</span>}
                  </li>
                ))}
              </ul>
              {pre.manual_actions?.length > 0 && (
                <div className="rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 p-3">
                  <div className="text-xs font-semibold text-[#FFB800] mb-1">Manual steps</div>
                  {pre.manual_actions.map((m, i) => <div key={i} className="text-xs text-zinc-300 mb-1"><b>{m.label}:</b> {m.action}</div>)}
                </div>
              )}
            </>
          )}
        </Panel>
      )}
    </div>
  );
}

function ListingTab({ a, refresh }) {
  const [sl, setSl] = useState(a.store_listing || {});
  const save = async () => { try { await api.patch(`/apps/${a.id}`, { data: { store_listing: sl } }); toast.success("Store listing saved"); refresh(); } catch { toast.error("Save failed"); } };
  const F = ({ k, label, area }) => area
    ? <textarea data-testid={`sl-${k}`} rows={3} value={sl[k] || ""} onChange={(e) => setSl({ ...sl, [k]: e.target.value })} placeholder={label} className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-sm text-white outline-none focus:border-[#276A6B]" />
    : <input data-testid={`sl-${k}`} value={sl[k] || ""} onChange={(e) => setSl({ ...sl, [k]: e.target.value })} placeholder={label} className="w-full rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-sm text-white outline-none focus:border-[#276A6B]" />;
  return (
    <Panel>
      <div className="space-y-3 max-w-2xl">
        <div><div className="text-xs text-zinc-500 mb-1">Title</div><F k="title" label="App title" /></div>
        <div><div className="text-xs text-zinc-500 mb-1">Short description</div><F k="short_desc" label="Short description" area /></div>
        <div><div className="text-xs text-zinc-500 mb-1">Full description</div><F k="full_desc" label="Full description" area /></div>
        <div><div className="text-xs text-zinc-500 mb-1">Privacy policy URL (required by Google Play)</div><F k="privacy_policy_url" label="https://…" /></div>
        <button data-testid="sl-save" onClick={save} className="rounded-full bg-[#276A6B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#174A4B]">Save Listing</button>
      </div>
    </Panel>
  );
}

function MonetizationTab({ a }) {
  const m = a.monetization || {};
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <Panel>
        <div className="flex items-center gap-2 mb-3"><CurrencyGbp size={20} className="text-[#6FA9A5]" /><h3 className="font-display font-bold text-white">Subscription</h3></div>
        <div className="space-y-2 text-sm">
          <Row l="Model" v="Free + PRO" /><Row l="PRO price" v={`£${m.pro_price}/month`} />
          <Row l="Free trial" v={`${m.trial_days} days`} /><Row l="Yearly ready" v={m.yearly_ready ? "Yes" : "No"} />
          <Row l="Billing" v="Google Play Billing" /><Row l="Product IDs" v={(m.product_ids || []).join(", ") || "—"} />
        </div>
        <p className="mt-3 text-xs text-[#FFB800]">Final prices come from Google Play configuration, not hard-coded. Server-side entitlement validation added with Play credentials (Phase 2).</p>
      </Panel>
      <Panel>
        <h3 className="font-display font-bold text-white mb-3">PRO features</h3>
        <ul className="text-sm text-zinc-300 space-y-1.5 list-disc pl-5">
          <li>Fly to Location</li><li>Unlimited Saved Places & Entrances</li>
          <li>Extended History</li><li>Full Driver Mode</li><li>Advanced location tools (later)</li>
        </ul>
      </Panel>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between border-b border-white/5 py-1.5"><span className="text-zinc-500">{l}</span><span className="text-white font-medium">{v}</span></div>;
